import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import * as videosApi from '../api/videos';
import { usePagedList } from '../lib/usePagedList';
import { useConfirm } from '../lib/confirm';
import { useToast } from '../components/toast';
import { useOrg } from '../lib/org';
import { ErrorAlert, Spinner, EmptyState, PagedListFooter, StatusPill, formatDate } from '../components/ui';
import { ui, cn } from '../lib/ui';

const PAGE_SIZE = 20;

const STATUS_OPTIONS = ['', 'uploading', 'processing', 'ready', 'failed', 'removed'];

function formatSize(bytes) {
  if (!bytes && bytes !== 0) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}

function formatDuration(seconds) {
  if (!seconds) return '—';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
}

export default function Videos() {
  const confirm = useConfirm();
  const toast = useToast();
  const { activeOrg } = useOrg();
  const [busy, setBusy] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const inputRef = useRef(null);

  const isOrgAdminOrMod = activeOrg && ['org-admin', 'org-moderator'].includes(activeOrg.role);

  const list = usePagedList(
    (cursor) => {
      const query = { limit: PAGE_SIZE };
      if (cursor) query.lastEvaluatedKey = cursor;
      if (statusFilter) query.status = statusFilter;
      return videosApi.listVideos(query)
        .then((d) => ({ items: d.videos || [], lastEvaluatedKey: d.lastEvaluatedKey }));
    },
    [statusFilter]
  );

  async function onUpload(event) {
    const file = event.target.files[0];
    if (!file) return;
    setBusy(true);
    try {
      await videosApi.uploadVideo(file);
      toast.success(`Uploaded "${file.name}". Processing will begin shortly.`);
      list.reload();
    } catch (err) {
      toast.error(err);
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  }

  async function onDelete(video) {
    const ok = await confirm(`Delete "${video.title}"? This cannot be undone.`);
    if (!ok) return;
    try {
      await videosApi.deleteVideo(video.videoId);
      toast.success('Video deleted.');
      list.removeItem(video.videoId, 'videoId');
    } catch (err) {
      toast.error(err);
    }
  }

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <h1 className="m-0 text-title-l">Videos</h1>
        {isOrgAdminOrMod && (
          <label className={cn(ui.btnPrimary, busy ? 'cursor-wait' : 'cursor-pointer')}>
            {busy ? 'Uploading…' : 'Upload video'}
            <input
              ref={inputRef}
              type="file"
              accept="video/mp4,video/webm,video/quicktime,video/x-msvideo"
              onChange={onUpload}
              disabled={busy}
              className="hidden"
            />
          </label>
        )}
      </div>

      <div className="mb-4">
        <label htmlFor="status-filter" className="mr-2">Filter by status:</label>
        <select
          id="status-filter"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          {STATUS_OPTIONS.map((s) => (
            <option key={s} value={s}>{s || 'All'}</option>
          ))}
        </select>
      </div>

      {list.loading && <Spinner />}
      {list.error && <ErrorAlert error={list.error} />}
      {!list.loading && !list.error && list.items.length === 0 && (
        <EmptyState>No videos yet.</EmptyState>
      )}

      {list.items.length > 0 && (
        <table className={ui.table}>
          <thead>
            <tr>
              <th>Title</th>
              <th>Status</th>
              <th>Duration</th>
              <th>Size</th>
              <th>Audience</th>
              <th>Uploaded</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {list.items.map((v) => (
              <tr key={v.videoId}>
                <td>
                  <Link to={`/videos/${v.videoId}`}>{v.title}</Link>
                </td>
                <td><StatusPill status={v.status} /></td>
                <td>{formatDuration(v.duration)}</td>
                <td>{formatSize(v.fileSize)}</td>
                <td>{v.audienceLevel}</td>
                <td>{formatDate(v.createdAt)}</td>
                <td>
                  {isOrgAdminOrMod && activeOrg.role === 'org-admin' && (
                    <button
                      className={cn(ui.btnDanger, ui.btnSm)}
                      onClick={() => onDelete(v)}
                    >
                      Delete
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <PagedListFooter
        hasMore={list.hasMore}
        loadingMore={list.loadingMore}
        loadMore={list.loadMore}
        error={list.error}
        reload={list.reload}
      />
    </div>
  );
}
